import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FilterBoxComponent } from '../filter-box/filter-box.component';
import { ExportacaoConfig, ListBoxComponent } from '../list-box/list-box.component';
import { ListaAcaoEngrenagem } from '../models/lista-acao-engrenagem.model';
import { ListaColunaConfig } from '../models/lista-coluna-config.model';
import { ListagemConsultaSolicitadaPayload } from '../models/listagem-consulta-solicitada.model';
import { ListaParametrosPaginacao } from '../models/lista-paginacao.model';
import { FlashMessageService } from '../../services/flash-message.service';

/**
 * Orquestra **TitleBox** + **FilterBox** + **ListBox** + **FlashMessage** num único fluxo:
 * abertura de filtros, primeira consulta ao iniciar, paginação, ordenação (server-side
 * opcional) e emissão de `consultaSolicitada` com snapshot de filtros + página + sort.
 *
 * **Quando usar:** telas “orquestrador” em que o desenvolvedor quer menos código na
 * página — a página pai só implementa `(consultaSolicitada)` e preenche `[itens]` /
 * `[totalRegistros]`. Não injeta `HttpClient`; quem chama API é sempre o componente pai.
 *
 * **Filtros:** o objeto `filtros` é mutado pelos campos projetados em
 * `orchestrator-filter-fields`; use tipos simples compatíveis com o payload da sua API.
 * Botões secundários do rodapé do filtro (ex.: “Limpar filtros”) podem ser projetados em
 * `orchestrator-filter-footer-extras`.
 *
 * **Evento principal:** `consultaSolicitada` emite payload com `pagina`, `tamanhoPagina`,
 * cópia de `filtros` e — em modo `ordenacaoServerSide` — também `ordenacao` (valor
 * backend da coluna) e `ordenacaoDesc`.
 *
 * @see ListBoxComponent Grade interna
 * @see FilterBoxComponent Painel de filtros (slot `filter-box-footer-extras`)
 */
@Component({
  selector: 'app-orchestrator-box',
  templateUrl: './orchestrator-box.component.html',
  styleUrls: ['./orchestrator-box.component.scss'],
})
export class OrchestratorBoxComponent implements OnInit {
  @Input() tituloLocal = '';

  /**
   * Referência ao objeto de filtro mutado nos campos projetados no FilterBox.
   * Objeto simples (ex.: { nome, categoria }) é compatível com o snapshot em consultaSolicitada.
   */
  @Input() filtros: Record<string, unknown> = {};

  @Input() itens: unknown[] = [];

  @Input() totalRegistros = 0;

  @Input() colunas: ListaColunaConfig[] = [];

  @Input() acoesEngrenagem: ListaAcaoEngrenagem<any>[] = [];

  @Input() opcoesTamanhoPagina: number[] = [10, 20, 50, 100];

  @Input() paginarColunas = false;

  @Input() colunasPorGrupo = 6;

  @Input() filtroTextoExibir = true;

  @Input() filtroTextoLabel: string | null = null;

  @Input() filtroTextoPlaceholder = 'Digite para pesquisar itens';

  @Input() mostrarBotaoFiltrar = true;

  /**
   * Link “Abrir filtros (código)” na seção da lista (demo de ViewChild + `abrir()`).
   * Desligue em telas de produto onde só o botão do título basta.
   */
  @Input() mostrarAbrirFiltrosCodigo = true;

  @Input() secaoListaTitulo = 'Lista';

  /** Página inicial 1-based (sincronizada com a primeira consulta). */
  @Input() paginaInicial = 1;

  @Input() tamanhoPaginaInicial = 10;

  /** Repassado ao {@link ListBoxComponent}. Veja docs daquele componente. */
  @Input() paginacaoClientSide = false;

  /** Repassado ao {@link ListBoxComponent}. Expressão JS avaliada por linha. */
  @Input() corLinhaCondicao = '';

  /** Repassado ao {@link ListBoxComponent}. */
  @Input() mostrarPaginador = true;

  /** Repassado ao {@link ListBoxComponent}. Mostra combo "Ordenar por" + radio. */
  @Input() ordenacaoExibir = true;

  /**
   * Quando `true`, o orquestrador escuta `(ordenacaoAlterada)` do `ListBox`,
   * armazena o estado em {@link ordenacaoAtual} / {@link ordenacaoDescAtual}
   * e dispara `consultaSolicitada` com `ordenacao` e `ordenacaoDesc`
   * preenchidos. Em modo client-side (default), o ListBox cuida sozinho.
   */
  @Input() ordenacaoServerSide = false;

  /**
   * Valor inicial do `ordenacao` enviado no primeiro `consultaSolicitada`
   * (deve corresponder a um `valorOrdenacaoBackend` de alguma coluna). Use
   * `ordenacaoPadrao: true` na coluna correspondente para alinhar a UI do
   * combo/radio com este valor.
   */
  @Input() ordenacaoInicial = '';

  /** Direção inicial: `true` = decrescente, `false` = crescente. */
  @Input() ordenacaoDescInicial = false;

  @Output()
  consultaSolicitada = new EventEmitter<
    ListagemConsultaSolicitadaPayload<Record<string, unknown>>
  >();

  /**
   * Repassa o evento do `ListBox` quando o usuário clica num botão
   * de coluna `modoCelula: 'botaoAcao'` (ex.: bypass, aprovar). A
   * página pai trata pela `coluna.id` recebida.
   */
  @Output() botaoAcaoClicado = new EventEmitter<{
    linha: unknown;
    coluna: ListaColunaConfig;
  }>();

  /**
   * Repassa o evento do `ListBox` quando a ordenação muda. Em modo
   * `ordenacaoServerSide`, o orquestrador também já dispara
   * `consultaSolicitada` automaticamente — escute este evento apenas
   * se quiser reagir além disso (ex.: persistir preferência do usuário).
   */
  @Output() ordenacaoAlterada = new EventEmitter<{
    idColuna: string;
    valorBackend?: string;
    desc: boolean;
  }>();

  /**
   * Repassa o evento do `ListBox` quando o usuário escolhe exportar
   * **todos** os registros (não apenas a página atual). A página pai
   * deve buscar todos os dados e chamar `exportarDados()` no
   * `ListBoxComponent` (acessível via {@link listaBox}).
   */
  @Output() exportarTodosSolicitado = new EventEmitter<ExportacaoConfig>();

  /**
   * Repassa o evento do `ListBox` quando o usuário clica num dos
   * ícones de uma coluna `modoCelula: 'iconesImagens'`. `iconeId` é o
   * identificador livre configurado no item (ex.: `'BOMBA'`).
   */
  @Output() iconeImagemClicado = new EventEmitter<{
    linha: unknown;
    coluna: ListaColunaConfig;
    iconeId: string;
  }>();

  filtroAberto = false;

  paginaAtual = 1;

  tamanhoPagina = 10;

  /** Estado atual da ordenação server-side (vai no payload de `consultaSolicitada`). */
  ordenacaoAtual = '';
  ordenacaoDescAtual = false;

  @ViewChild(FilterBoxComponent)
  filtroPainel?: FilterBoxComponent;

  /** Acesso ao `ListBox` interno — útil para chamar `exportarDados()` etc. */
  @ViewChild(ListBoxComponent)
  listaBox?: ListBoxComponent;

  constructor(private flash: FlashMessageService) {}

  ngOnInit(): void {
    this.paginaAtual = this.normalizarPagina(this.paginaInicial);
    this.tamanhoPagina = this.normalizarTamanho(this.tamanhoPaginaInicial);
    this.ordenacaoAtual = this.ordenacaoInicial;
    this.ordenacaoDescAtual = this.ordenacaoDescInicial;
    /*
     * O mock (of) entrega dados de forma síncrona; emitir no mesmo ciclo faz o pai
     * atualizar [itens] durante a verificação inicial e gera NG0100 no dev mode.
     */
    Promise.resolve().then(() => this.emitirConsulta());
  }

  alternarFiltroTitulo(): void {
    this.flash.limpar();
    this.filtroAberto = !this.filtroAberto;
  }

  onAplicarFiltroPainel(): void {
    this.flash.limpar();
    this.paginaAtual = 1;
    this.emitirConsulta();
  }

  onPaginacaoAlteradaListBox(params: ListaParametrosPaginacao): void {
    this.flash.limpar();
    const p = Math.floor(Number(params.pagina));
    const t = Math.floor(Number(params.tamanhoPagina));
    this.paginaAtual = Number.isFinite(p) && p >= 1 ? p : 1;
    this.tamanhoPagina =
      Number.isFinite(t) && t >= 1 ? t : this.tamanhoPagina || 10;
    this.emitirConsulta();
  }

  onOrdenacaoAlteradaListBox(evento: {
    idColuna: string;
    valorBackend?: string;
    desc: boolean;
  }): void {
    this.ordenacaoAlterada.emit(evento);
    if (!this.ordenacaoServerSide) {
      return;
    }
    /*
     * Volta para a primeira página: ao mudar critério de ordenação,
     * faz pouco sentido manter o usuário na página N anterior.
     */
    this.flash.limpar();
    this.ordenacaoAtual = evento.valorBackend || '';
    this.ordenacaoDescAtual = evento.desc;
    this.paginaAtual = 1;
    this.emitirConsulta();
  }

  onBotaoAcaoListBox(evento: { linha: unknown; coluna: ListaColunaConfig }): void {
    this.botaoAcaoClicado.emit(evento);
  }

  onExportarTodosListBox(config: ExportacaoConfig): void {
    this.exportarTodosSolicitado.emit(config);
  }

  abrirFiltrosPorCodigo(): void {
    this.filtroPainel?.abrir();
  }

  private emitirConsulta(): void {
    const payload: ListagemConsultaSolicitadaPayload<Record<string, unknown>> = {
      pagina: this.paginaAtual,
      tamanhoPagina: this.tamanhoPagina,
      filtros: { ...(this.filtros as object) } as Record<string, unknown>,
    };
    if (this.ordenacaoServerSide) {
      payload.ordenacao = this.ordenacaoAtual;
      payload.ordenacaoDesc = this.ordenacaoDescAtual;
    }
    this.consultaSolicitada.emit(payload);
  }

  private normalizarPagina(p: number): number {
    const n = Math.floor(Number(p));
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }

  private normalizarTamanho(t: number): number {
    const n = Math.floor(Number(t));
    return Number.isFinite(n) && n >= 1 ? n : 10;
  }
}
